import { ObjectUtils } from '@utils/helpers/index';

export {
	asyncForEach,
	checkForSingleValueArgAndReturnAsArray,
	checkForArrayArgAndReturnAsSingleValue,
	insertAtIndex
};

async function asyncForEach( array: any[], callback: ( element: any, index?: number, array?: any[] ) => Promise<any> ) {
	/*
	 * If the input array is nullish, the function returns a -resolving- promise
	 * to avoid the need for calling the function inside a try-catch statement.
	 * So the function will simply do an early return in this case.
	 */
	if ( !array ) { return new Promise( resolve => resolve( null ) ); }

	for ( let index = 0; index < array.length; index++ ) {
		await callback( array[ index ], index, array );
	}
}

function checkForArrayArgAndReturnAsSingleValue<T>( valueArg: T | T[], valueFromIndex: number = 0 ): T {
	if ( ObjectUtils.isNullish( valueArg ) ) { return null; }

	return Array.isArray( valueArg )
				 ? valueArg[ valueFromIndex ] ?? null
				 : valueArg;
}

function checkForSingleValueArgAndReturnAsArray<T>( valueArg: T | T[] ): T[] {
	if ( ObjectUtils.isNullish( valueArg ) ) { return null; }

	return Array.isArray( valueArg )
				 ? valueArg
				 : [ valueArg ];
}

/**
 * Inserts array elements into an array at a specific index
 *
 * @param sourceArr - the array to insert elements into
 * @param insertArr - the array with the elements to be inserted
 * @param insertAt - the index of `sourceArr` to insert the array elements at
 * @param options - some optional parameters
 * @param options.appendOnOutOfBounds - if `true` and `insertAt` is outside the source array's bounds, the new elements are appended at the end of `sourceArr`
 * @param options.removeFromSource - if `true` and given index is within the source array's bounds, the original element at the insert index of `sourceArr` is removed
 *
 * @return `sourceArr` with new items inserted or unchanged if
 * * the insert index is outside the array's bounds and the `appendOnOutOfBounds` flag is falsy
 * * or one of the mandatory parameters is nullish
 */
function insertAtIndex(
	sourceArr: any[],
	insertArr: any[],
	insertAt: number,
	options: { appendOnOutOfBounds?: boolean; removeFromSource?: boolean } = {}
): any[] {
	const {
		appendOnOutOfBounds,
		removeFromSource
	} = options;

	if ( ObjectUtils.isNullish( sourceArr ) || ObjectUtils.isNullish( insertArr ) || ObjectUtils.isNullish( insertAt ) ) {
		return sourceArr;
	}

	const isInsertIndexWithinBounds = insertAt > -1 && insertAt < sourceArr?.length;
	const insertIndexPrecursors = sourceArr.slice( 0, insertAt );
	const insertIndexSuccessors = sourceArr.slice( removeFromSource ? insertAt + 1 : insertAt );
	return isInsertIndexWithinBounds
									? insertIndexPrecursors.concat( insertArr ).concat( insertIndexSuccessors )
									: appendOnOutOfBounds
										? sourceArr.concat( insertArr )
										: sourceArr;
}
